import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'react-native-paper';

import { AttachmentIcon, CameraIcon, PhotoIcon } from '@/assets';
import type { ImagePickType } from '@/features/media/types/picker';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { ExtendedMenuProps } from '@/shared/components/ExtendedMenu';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { isWeb } from '@/shared/utils/platform';

type Props = {
  canPickImage?: boolean;
  disablePickImage?: boolean;
  onPickImage?: (pick: ImagePickType) => void;
  canPickDocument?: boolean;
  disablePickDocument?: boolean;
  onPickDocument?: () => void;
} & Omit<ExtendedMenuProps, 'children'>;

/**
 * For now only single select is possible
 * Todo: add support for multiselect
 */
export const MediaMenu: FC<Props> = ({
  canPickImage = false,
  disablePickImage = true,
  onPickImage,
  canPickDocument = false,
  disablePickDocument = true,
  onPickDocument,
  onDismiss,
  ...rest
}) => {
  const { t } = useTranslation(LocalizationNameSpace.MEDIA, {
    keyPrefix: 'components.media-menu'
  });

  return (
    <ExtendedMenu onDismiss={onDismiss} {...rest}>
      {canPickImage && (
        <Menu.Item
          leadingIcon={({ color, size }) => (
            <PhotoIcon fill={color} width={size} height={size} />
          )}
          disabled={disablePickImage}
          onPress={() => {
            onDismiss();
            onPickImage?.('select');
          }}
          title={t('select')}
        />
      )}
      {canPickImage && !isWeb && (
        <Menu.Item
          leadingIcon={({ color, size }) => (
            <CameraIcon fill={color} width={size} height={size} />
          )}
          disabled={disablePickImage}
          onPress={() => {
            onDismiss();
            onPickImage?.('take');
          }}
          title={t('take')}
        />
      )}
      {canPickDocument && (
        <Menu.Item
          leadingIcon={({ color, size }) => (
            <AttachmentIcon fill={color} width={size} height={size} />
          )}
          disabled={disablePickDocument}
          onPress={() => {
            onDismiss();
            onPickDocument?.();
          }}
          title={t('document')}
        />
      )}
    </ExtendedMenu>
  );
};
